import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "app/slice/selectors";

import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuContent,
} from "app/components/ui/navigation-menu";

const getNavItems = (userExists: boolean) => {
  const plansItems = userExists
    ? [
        {
          to: "/network-coverage#triple-network-technology",
          label: "Multi Network",
        },
        { to: "/family-plans", label: "Family Plans" },
        { to: "/fixed-phone", label: "Fixed Phone" },
      ]
    : [
        { to: "/network-plan", label: "Single Network" },
        {
          to: "/network-coverage#triple-network-technology",
          label: "Multi Network",
        },
        { to: "/family-plans", label: "Family Plans" },
        { to: "/fixed-phone", label: "Fixed Phone" },
      ];

  return [
    {
      type: "dropdown",
      label: "Plans",
      width: "w-[3.85rem]",
      items: [
        { to: "/network-coverage#coverage-map", label: "Single Network" },
        {
          to: "/network-coverage#triple-network-technology",
          label: "Multi Network",
        },
        { to: "/family-plans", label: "Family Plans" },
        { to: "/fixed-phone", label: "Fixed Phone" },
      ],
    },
    {
      type: "dropdown" as const,
      label: "Security",
      width: "w-[5rem]",
      items: [
        { to: "/digital-security", label: "Online Security Plans" },
        { to: "/sim-swap-protection", label: "SIM Swap Protection" },
        {
          to: "/sim-swap-protection#security-protocols",
          label: "Multi-layer Fraud Prevention",
        },
      ],
    },
    { type: "link" as const, to: "/fixed-phone", label: "Home Phone" },
    {
      type: "link" as const,
      to: "/platform-drivers",
      label: "For Drivers",
    },
    { type: "link" as const, to: "/pricing", label: "Pricing" },
    { type: "link" as const, to: "/how-to-switch", label: "How to Switch" },
    { type: "link" as const, to: "/why-vle-mobile", label: "Why VLE Mobile" },
  ];
};

export const MainNavigation = () => {
  const user = useSelector(selectUser);
  const location = useLocation();

  return (
    <nav className="hidden items-center lg:flex">
      <NavigationMenu>
        <NavigationMenuList className="[@media(max-width:1070px)]:gap-3 gap-5 xl:gap-7">
          {getNavItems(!!user).map((item) => {
            if (item.type === "dropdown") {
              return (
                <NavigationMenuItem key={item.label}>
                  <NavigationMenuTrigger
                    className={`bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent h-4 text-[0.785rem] xl:text-sm leading-5 hover:text-[#FE4D01] whitespace-nowrap text-[#464646] font-semibold data-[state=open]:text-[#1D1D1B] data-[state=open]:font-bold ${item.width}`}
                  >
                    {item.label}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent className="p-[0.1875rem] pr-[0.3125rem] pb-[0.3125rem] md:w-64">
                    <ul className="grid gap-0.5">
                      {item.items?.map((subItem) => (
                        <li key={subItem.to}>
                          <NavigationMenuLink
                            asChild
                            className={`relative hover:bg-[#FE4D0114] focus:bg-[#FE4D0114] data-[active=true]:bg-[#FE4D0114] data-[active=true]:hover:bg-[#FE4D0114] data-[active=true]:focus:bg-[#FE4D0114] px-2.5 lg:px-3 xl:px-3.5 py-1 lg:py-1.5 text-[0.785rem] xl:text-sm leading-5 whitespace-nowrap text-[#353531] hover:text-[#353531] focus:text-[#353531] data-[active=true]:text-[#FE4D01] font-medium ${
                              location.pathname + (location.hash || "") ===
                              subItem.to
                                ? "data-[active=true]"
                                : ""
                            }`}
                            data-active={
                              location.pathname + (location.hash || "") ===
                              subItem.to
                            }
                            active={
                              location.pathname + (location.hash || "") ===
                              subItem.to
                            }
                          >
                            <NavLink to={subItem.to}>
                              {subItem.label}
                              {location.pathname + (location.hash || "") ===
                                subItem.to && (
                                <img
                                  src="/assets/tick-orange.svg"
                                  alt="Tick"
                                  className="shrink-0 size-4 xl:size-[1.125rem] absolute right-2 lg:right-2.5 xl:right-3 top-1/2 -translate-y-1/2"
                                  aria-hidden="true"
                                />
                              )}
                            </NavLink>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              );
            }

            return (
              <NavigationMenuItem key={item.to}>
                <NavLink
                  to={item.to ?? ""}
                  end={item.to === "/fixed-phone"}
                  className={({ isActive }) =>
                    `text-[0.785rem] xl:text-sm leading-5 hover:text-[#FE4D01] whitespace-nowrap ${
                      isActive
                        ? "text-[#1D1D1B] font-bold"
                        : "text-[#464646] font-semibold"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </NavigationMenuItem>
            );
          })}
        </NavigationMenuList>
      </NavigationMenu>
    </nav>
  );
};
